/*  eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import { reset } from "../analytics/posthog";
import {
  abortSessionRefresh,
  isSessionRejected,
  refreshSessionOnce,
  REFRESH_TIMEOUT_MS,
} from "../auth/sessionRefresh";
import { markSessionLoggedOut } from "../auth/sessionRestore";
import { isPublicAuthFlowPath } from "../utils/publicAuthFlowPaths";
import { axiosClient, API_BASE_URL } from "./axiosClient";

let expiredSessionHandledAt = 0;

// ----- Circuit breaker: prevent infinite redirect loops -----
const REDIRECT_LIMIT = 3;
const REDIRECT_WINDOW_MS = 10_000; // 10 seconds
let redirectTimestamps: number[] = [];

function isRedirectLooping(): boolean {
  const now = Date.now();
  // keep only timestamps within the window
  redirectTimestamps = redirectTimestamps.filter(
    (t) => now - t < REDIRECT_WINDOW_MS,
  );
  return redirectTimestamps.length >= REDIRECT_LIMIT;
}

function recordRedirect(): void {
  redirectTimestamps.push(Date.now());
}

// ----- Redirect helper (mockable in tests via authInterceptorHelpers) -----
export const authInterceptorHelpers = {
  redirectToSignin(): void {
    const path = window.location.pathname;
    if (isPublicAuthFlowPath(path)) {
      return;
    }
    if (
      path === "/signin" ||
      path === "/login" ||
      path === "/signup" ||
      path.startsWith("/signup/") ||
      path === "/forgot-password" ||
      path === "/reset-password" ||
      path.startsWith("/oauth/")
    ) {
      return;
    }
    window.location.replace("/signin?session=expired");
  },
  refreshSession(): Promise<unknown> {
    return refreshClient.post("/auth/refresh", {}, {
      withCredentials: true,
      timeout: REFRESH_TIMEOUT_MS,
    });
  },
  clearExpiredSessionIdentity(): void {
    markSessionLoggedOut();
    reset();
  },
  latestErrorHandler: null as null | ((error: any) => Promise<unknown>),
};

// ----- Structured auth logging -----
const AUTH_LOG_PREFIX = "[auth-interceptor]";

// Avoid direct `import.meta` at top-level so Jest can parse
// this file without ESM. The flag is resolved lazily.
let _isDev: boolean | undefined;
function isDev(): boolean {
  if (_isDev === undefined) {
    try {
      const importObject = (
        globalThis as typeof globalThis & {
          import?: { meta?: { env?: { DEV?: boolean } } };
        }
      ).import;
      _isDev = Boolean(importObject?.meta?.env?.DEV);
    } catch {
      _isDev = false;
    }
  }
  return _isDev;
}

function authLog(
  level: "info" | "warn" | "error",
  event: string,
  details?: Record<string, unknown>,
): void {
  const msg = `${AUTH_LOG_PREFIX} ${event}`;
  const payload = details
    ? { ...details, timestamp: new Date().toISOString() }
    : undefined;
  if (level === "error") {
    console.error(msg, payload ?? "");
  } else if (level === "warn") {
    console.warn(msg, payload ?? "");
  } else if (isDev()) {
    // info logs only in development
    console.info(msg, payload ?? "");
  }
}

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Forget the current refresh attempt. Call this on explicit logout so later
 * 401s don't reuse a refresh that belonged to the old session.
 */
export function abortRefreshAndRejectQueue() {
  abortSessionRefresh();
  expiredSessionHandledAt = 0;
}

function handleExpiredSession(refreshErr: unknown): Promise<never> {
  // Concurrent 401s share one refresh; only the first one clears and redirects.
  const now = Date.now();
  if (now - expiredSessionHandledAt < REDIRECT_WINDOW_MS) {
    return Promise.reject(refreshErr);
  }
  expiredSessionHandledAt = now;

  // Circuit breaker: stop redirecting if we've already redirected recently
  if (isRedirectLooping()) {
    authInterceptorHelpers.clearExpiredSessionIdentity();
    authLog("error", "redirect-circuit-breaker-tripped", {
      count: redirectTimestamps.length,
      windowMs: REDIRECT_WINDOW_MS,
    });
    return Promise.reject(refreshErr);
  }

  recordRedirect();
  authInterceptorHelpers.clearExpiredSessionIdentity();
  try {
    localStorage.removeItem("ilovemacumba:auth:currentUser");
    localStorage.removeItem("ilovemacumba:terreiro:current");
    localStorage.removeItem("ilovemacumba:medium:current");
  } catch {
    /* ignore */
  }
  authLog("info", "redirect-to-signin");
  authInterceptorHelpers.redirectToSignin();

  return Promise.reject(refreshErr);
}

export function setupAuthInterceptor() {
  const onRejected = async (error: any) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    if (!originalRequest) return Promise.reject(error);

    const url = originalRequest.url || "";
    const skipRefreshHeader =
      originalRequest.headers &&
      (originalRequest.headers["x-skip-refresh"] ||
        originalRequest.headers["X-Skip-Refresh"]);

    // Defensive: never attempt refresh for refresh/logout/me or when caller asked to skip
    if (
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout") ||
      url.includes("/auth/passkeys/login/") ||
      url.includes("/profile/me") ||
      skipRefreshHeader
    ) {
      authLog("info", "skip-refresh", {
        url,
        reason: "excluded-url-or-header",
      });
      return Promise.reject(error);
    }

    // enforce a retry cap per request to prevent infinite loops
    const retryCount = (originalRequest as any)._retryCount || 0;

    if (status === 401 && retryCount < 1) {
      (originalRequest as any)._retryCount = retryCount + 1;
      authLog("info", "refresh-start", { url });

      try {
        // Use refreshClient (no interceptor) to avoid recursion and include cookies
        await refreshSessionOnce(() => authInterceptorHelpers.refreshSession());
      } catch (refreshErr) {
        const refreshStatus = (refreshErr as any)?.response?.status;
        if (!isSessionRejected(refreshErr)) {
          // 502 on a cold start, timeout or offline: the session may still be
          // valid, so keep it and surface the original error.
          authLog("warn", "refresh-unavailable", { status: refreshStatus });
          return Promise.reject(error);
        }
        authLog("warn", "refresh-failed", { status: refreshStatus });
        return handleExpiredSession(refreshErr);
      }

      authLog("info", "refresh-success");
      return axiosClient(originalRequest);
    }

    return Promise.reject(error);
  };

  authInterceptorHelpers.latestErrorHandler = onRejected;

  axiosClient.interceptors.response.use((res) => res, onRejected);
}
